import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import {
  Button,
  Card,
  Pagination,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from "@rumbledirect/ui-v2";
import type { ContractMonitorRow } from "@/contractMonitor/contractMonitor";
import {
  type ContractMonitorColumn,
  getContractMonitorColumns,
} from "@/contractMonitor/contractMonitorColumns";
import { getContractVehicleTitle } from "@/contractMonitor/contractMonitorFields";
import { isContractMonitorStatusFilter } from "@/contractMonitor/contractMonitorLabels";
import {
  defaultContractMonitorPeriod,
  isPeriodOrdered,
} from "@/contractMonitor/contractMonitorPeriod";
import {
  ContractMonitorFilters,
  type ContractMonitorFilterValues,
} from "@/contractMonitor/ContractMonitorFilters";
import { useGetAdminContractMonitorVehicleTenure } from "@/lib/api/generated/contract-monitor/contract-monitor";
import { cn } from "@rumbledirect/ui-v2/cn";
import { toPeriodDate } from "@/lib/periodDate";

/** The list columns are static, so they are built once at module load. */
const CONTRACT_MONITOR_COLUMNS = getContractMonitorColumns();

/** Matches `paginationItemsPerPage` on the backend's `ContractMonitorResource`. */
const ITEMS_PER_PAGE = 50;

/** Enough skeleton rows to fill the fold without pretending to know the count. */
const SKELETON_ROW_KEYS = Array.from(
  { length: 10 },
  (_, index) => `contract-monitor-skeleton-${index}`,
);

interface ContractMonitorSearch extends ContractMonitorFilterValues {
  readonly page: number;
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

export const Route = createFileRoute("/_authenticated/contract-monitor")({
  // The whole filter state lives in the URL so a view can be shared or
  // bookmarked. The period is filled in with a default rather than left empty,
  // because the backend requires both bounds.
  validateSearch: (search: Record<string, unknown>): ContractMonitorSearch => {
    const page = Number(search.page);
    const fallback = defaultContractMonitorPeriod();
    const status = search.status;

    return {
      page: Number.isInteger(page) && page >= 1 ? page : 1,
      activeFrom: toPeriodDate(search.activeFrom) ?? fallback.activeFrom,
      activeUntil: toPeriodDate(search.activeUntil) ?? fallback.activeUntil,
      site: toOptionalString(search.site),
      leaseCompany: toOptionalString(search.leaseCompany),
      status: isContractMonitorStatusFilter(status) ? status : undefined,
    };
  },
  component: ContractMonitorPage,
});

function ContractMonitorPage(): ReactNode {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { page, activeFrom, activeUntil, site, leaseCompany, status } = search;

  const periodOrdered = isPeriodOrdered(search);

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminContractMonitorVehicleTenure(
      {
        page,
        activeFrom,
        activeUntil,
        ...(site ? { site } : {}),
        ...(leaseCompany ? { leaseCompany } : {}),
        ...(status ? { "status[]": [status] } : {}),
      },
      {
        query: {
          placeholderData: keepPreviousData,
          // An inverted period is a typo, not a question: the filter bar says
          // so and the request stays unsent.
          enabled: periodOrdered,
        },
      },
    );

  const columns = CONTRACT_MONITOR_COLUMNS;
  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;
  const columnCount = columns.length + 1;

  /** Any filter change resets to page 1; page 7 of the old result set is meaningless. */
  const applyFilters = (patch: Partial<ContractMonitorFilterValues>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Contractmonitor
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          De leasecontracten die het wagenpark in de gekozen periode bezetten.
          Beëindigde contracten blijven buiten beschouwing.
        </p>
      </div>

      <ContractMonitorFilters values={search} onChange={applyFilters} />

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              {columns.map((column) => (
                <Th key={column.id} className={column.visibilityClassName}>
                  {column.header}
                </Th>
              ))}
              <Th className="w-12">
                <span className="sr-only">Voertuig openen</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <ContractMonitorRows
              columns={columns}
              columnCount={columnCount}
              isPending={isPending && periodOrdered}
              isError={isError}
              periodOrdered={periodOrdered}
              members={members}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}
    </div>
  );
}

function ContractMonitorRows({
  columns,
  columnCount,
  isPending,
  isError,
  periodOrdered,
  members,
  onRetry,
}: {
  readonly columns: readonly ContractMonitorColumn[];
  readonly columnCount: number;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly periodOrdered: boolean;
  readonly members: readonly ContractMonitorRow[] | undefined;
  readonly onRetry: () => void;
}): ReactNode {
  if (!periodOrdered) {
    return (
      <Tr>
        <Td colSpan={columnCount} className="py-10 text-center">
          <p className="text-muted-foreground">
            Kies een periode die vooruit loopt om contracten te zien.
          </p>
        </Td>
      </Tr>
    );
  }

  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        {columns.map((column) => (
          <Td key={column.id} className={column.visibilityClassName}>
            <Skeleton className="h-4 w-24" />
          </Td>
        ))}
        <Td>
          <Skeleton className="h-4 w-4" />
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={columnCount} className="py-10 text-center">
          <div className="space-y-3" role="alert">
            <p className="text-muted-foreground">
              We konden de contracten niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (!members || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={columnCount} className="py-10 text-center">
          <p className="text-muted-foreground">
            Geen contracten in deze periode.
          </p>
        </Td>
      </Tr>
    );
  }

  return members.map((row) => (
    <Tr key={row.id}>
      {columns.map((column) => (
        <Td
          key={column.id}
          className={cn(column.visibilityClassName, column.cellClassName)}
        >
          {column.cell(row)}
        </Td>
      ))}
      <Td className="text-right">
        {/*
          There is no contract detail page; the contract is managed on the
          vehicle, so that is where the row leads.
        */}
        {row.vehicle ? (
          <Link
            to="/vehicles/$vehicleId"
            params={{ vehicleId: row.vehicle }}
            search={{ tab: "gegevens" }}
            aria-label={`Voertuig ${
              row.licensePlate ?? getContractVehicleTitle(row)
            } openen`}
            className="inline-grid size-6 place-items-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </Link>
        ) : null}
      </Td>
    </Tr>
  ));
}
